import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Loader2, AlertTriangle } from 'lucide-react';
import { useDebounce } from 'use-debounce';
import { cn } from '@/lib/utils';

import { SearchBar } from '@/components/ui/SearchBar';
import { RunnerCard } from '@/components/runners/RunnerCard';
import { PageContent } from '@/components/layout/PageContent';
import { useToast } from '@/components/ui/Toast';
import { GET_CONTEXT_RACE, SEARCH_RUNNERS } from '@/graphql/queries';
import { ADD_RUNNER, DELETE_RUNNER } from '@/graphql/mutations';
import type { SearchedRunner } from '@/gql/graphql';

export function AddRunner({ raceId }: { raceId: string }) {
    const { t } = useTranslation('races');
    const { toast } = useToast();
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch] = useDebounce(searchTerm, 500);
    const [processingUris, setProcessingUris] = useState<string[]>([]);

    const { data: raceData } = useQuery(GET_CONTEXT_RACE, { variables: { raceId } });

    const totalRunners = raceData?.race?.totalRunners;
    const runnerCount = raceData?.race?.runnerCount ?? 0;
    const isLimitReached = totalRunners != null && runnerCount >= totalRunners;

    const { data: searchData, loading: searching } = useQuery(SEARCH_RUNNERS, {
        variables: { input: { name: debouncedSearch } },
        // skip: debouncedSearch.length < 3
    });

    const [addRunner] = useMutation(ADD_RUNNER, {
        refetchQueries: ['GetContextRace', 'GetRaceDetails', 'GetRaceRunnersForAdd']
    });
    const [deleteRunner] = useMutation(DELETE_RUNNER, {
        refetchQueries: ['GetContextRace', 'GetRaceDetails', 'GetRaceRunnersForAdd']
    });

    const handleToggleRunner = async (searchedRunner: SearchedRunner) => {
        const uri = searchedRunner.utmbUri;
        if (!uri || processingUris.includes(uri)) return;

        setProcessingUris((prev) => [...prev, uri]);

        try {
            const existingRunner = raceData?.race?.runners?.find((r) => r.utmbUri === uri);

            if (existingRunner?.id) {
                await deleteRunner({ variables: { id: existingRunner.id } });
            } else {
                await addRunner({ variables: { input: { raceId, utmbUri: uri } } });
            }
        } catch {
            toast(t('addRunner.error'), 'error');
        } finally {
            setProcessingUris((prev) => prev.filter((id) => id !== uri));
        }
    };

    return (
        <>
            <div className="sticky top-[65px] z-30 bg-white/95 backdrop-blur-sm border-b border-slate-100 shadow-sm transition-all">
                <div className="max-w-3xl mx-auto px-4 py-3">
                    <SearchBar
                        className="bg-slate-50 border-slate-200"
                        placeholder={t('addRunner.search')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onClear={() => setSearchTerm('')}
                        isLoading={searching}
                        autoFocus
                    />
                </div>
            </div>

            <PageContent width="narrow" className="pt-6 pb-24 space-y-4">
                {isLimitReached && (
                    <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-2xl">
                        <AlertTriangle size={18} className="text-amber-500 flex-shrink-0" />
                        <p className="text-sm font-medium text-amber-700">
                            {t('addRunner.limitReached', { count: totalRunners })}
                        </p>
                    </div>
                )}

                {searchData?.searchRunners?.map((runner) => {
                    const isAdded = raceData?.race?.runners?.some(r => r.utmbUri === runner.utmbUri);
                    const isProcessing = processingUris.includes(runner.utmbUri || "");

                    return (
                        <RunnerCard
                            key={runner.utmbId}
                            runner={runner}
                            isHighlight={isAdded}
                            className={cn(
                                "border-0 shadow-sm ring-1 ring-slate-100 transition-all",
                                isAdded && "ring-emerald-200 bg-emerald-50/50"
                            )}
                            action={
                                <button
                                    onClick={() => handleToggleRunner(runner)}
                                    disabled={isProcessing || (!isAdded && isLimitReached)}
                                    className={cn(
                                        "w-10 h-10 flex items-center justify-center rounded-xl transition-all active:scale-90",
                                        isProcessing ? "bg-slate-50 text-slate-400 cursor-wait" :
                                            !isAdded && isLimitReached ? "bg-slate-100 text-slate-300 cursor-not-allowed" :
                                                isAdded
                                                    ? "bg-white text-red-500 shadow-sm ring-1 ring-red-100 hover:bg-red-50"
                                                    : "bg-slate-900 text-white shadow-md shadow-slate-200 hover:bg-emerald-600"
                                    )}
                                >
                                    {isProcessing ? <Loader2 size={18} className="animate-spin" /> : isAdded ? <Trash2 size={18} /> : <Plus size={20} />}
                                </button>
                            }
                        />
                    );
                })}

                {searchTerm && searchData?.searchRunners?.length === 0 && !searching && (
                    <div className="text-center py-12 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                        <p className="text-slate-400 font-medium">{t('addRunner.empty', { search: searchTerm })}</p>
                    </div>
                )}
            </PageContent>

        </>
    );
}
